import React from "react";
import { useSelector } from "react-redux";

import { cx } from "~/tools/class-variance";
import { type Interval, endOfDay, getTime, isAfter, toDate } from "~/tools/date-time";
import { useNow } from "~/tools/use-now";

import { selectDocLinkUrlPrefix } from "../../../../../app/state/config/ConfigSelectors";
import { SpecificRange } from "../../../../../app/state/view/timeline/TimeRange";
import { notification } from "../../../notification/Notification";
import { RangePicker } from "../../../rangePicker/DatePicker";
import { warningMessageHalfYearLimit } from "../../TimelineUtils";

import { TimeRangeSelectorSection } from "./TimeRangeSelectorSection";

export interface TimeRangeSelectorContentCustomProps {
	onChange: (timeRange: SpecificRange) => void;
	onClear: VoidFunction;
	defaultValue?: Interval;
}

export const TimeRangeSelectorContentCustom: React.FC<TimeRangeSelectorContentCustomProps> = ({
	onChange,
	onClear,
	defaultValue,
}) => {
	const docLinkUrlPrefix = useSelector(selectDocLinkUrlPrefix);

	const endOfToday = useNow({
		// should convert to time so the hook causes re-render only when the day changes
		transform: (now) => getTime(endOfDay(now)),
	});

	return (
		<TimeRangeSelectorSection title="Custom interval">
			<RangePicker
				className={cx(
					"py-0",
					"[&_.ant-picker-input>input]:h-6.75 [&_.ant-picker-input>input]:text-13 [&_.ant-picker-input>input]:text-gray-800",
					"[&_.ant-picker-separator]:flex",
				)}
				defaultValue={defaultValue == null ? undefined : [toDate(defaultValue.start), toDate(defaultValue.end)]}
				disabledDate={(date) => isAfter(date, endOfToday)}
				format="DD.MM.YYYY, HH:mm:ss"
				onChange={(range) => {
					if (range == null) {
						onClear();
						return;
					}

					const [start, end] = range ?? [];

					if (start != null && end != null) {
						const newTimeRange = new SpecificRange(start, end);

						// we limit the dates until and of today
						// so it still possible to select seconds, minutes, and hours in the future
						if (newTimeRange.isInFuture()) {
							notification.warning({
								duration: 7,
								message: "Range should not be in the future",
							});
						}
						// we don't limit how wide the selection can be, so we need to check it here
						else if (newTimeRange.isWithinLimit()) {
							onChange(newTimeRange);
						} else {
							warningMessageHalfYearLimit(docLinkUrlPrefix);
						}
					}
				}}
				showTime
			/>
		</TimeRangeSelectorSection>
	);
};
